import { LitsError } from '../../errors'
import type { Any, Obj } from '../../interface'
import type { AstNode, SpecialExpressionNode } from '../../parser/types'
import { isUnknownRecord } from '../../typeGuards'
import { isSpreadNode } from '../../typeGuards/astNode'
import { assertString } from '../../typeGuards/string'
import type { MaybePromise } from '../../utils/maybePromise'
import { chain } from '../../utils/maybePromise'
import type { BuiltinSpecialExpression, FunctionDocs } from '../interface'
import type { specialExpressionTypes } from '../specialExpressionTypes'

export type ObjectNode = SpecialExpressionNode<[typeof specialExpressionTypes['object'], AstNode[]]>

const docs: FunctionDocs = {
  category: 'special-expression',
  returns: {
    type: 'object',
  },
  args: {
    kvps: {
      type: 'any',
      rest: true,
      description: 'key - value pairs, where key is a string',
    },
  },
  variants: [
    { argumentNames: ['kvps'] },
  ],
  description: 'Constructs a new object. Object members are created from the $kvps key-value pairs. Requires an even number of arguments.',
  examples: [
    'object()',
    `
let default = {
  type: "Person",
  name: "John Doe",
  age: 42
};

{
  ...default,
  name: "Lisa"
}`,
    'object("x", 10, "y", true, "z", "A string")',
    '{}',
    '{ a: 1, b: 2 }',
  ],
  hideOperatorForm: true,
}

export const objectSpecialExpression: BuiltinSpecialExpression<Any, ObjectNode> = {
  arity: {},
  docs,
  evaluate: (node, contextStack, { evaluateNode }) => {
    const result: Obj = {}
    const params = node[1][1]

    function processEntry(i: number): MaybePromise<Obj> {
      if (i >= params.length)
        return result
      const keyNode = params[i]!
      if (isSpreadNode(keyNode)) {
        return chain(evaluateNode(keyNode[1], contextStack), (spreadObject) => {
          if (!isUnknownRecord(spreadObject)) {
            throw new LitsError('Spread value is not an object', keyNode[2])
          }
          Object.assign(result, spreadObject)
          return processEntry(i + 1)
        })
      }
      else {
        const valueNode = params[i + 1]
        if (valueNode === undefined) {
          throw new LitsError('Missing value for key', keyNode[2])
        }
        return chain(evaluateNode(keyNode, contextStack), (key) => {
          return chain(evaluateNode(valueNode, contextStack), (value) => {
            assertString(key, keyNode[2])
            result[key] = value
            return processEntry(i + 2)
          })
        })
      }
    }

    return processEntry(0)
  },
  evaluateAsNormalExpression: (params, sourceCodeInfo) => {
    const result: Obj = {}

    for (let i = 0; i < params.length; i += 2) {
      const key = params[i]
      const value = params[i + 1]
      assertString(key, sourceCodeInfo)
      result[key] = value ?? null
    }

    return result
  },
  getUndefinedSymbols: (node, contextStack, { getUndefinedSymbols, builtin, evaluateNode }) => getUndefinedSymbols(node[1][1], contextStack, builtin, evaluateNode),
}
